import { requestingAds } from '../actions/adActions'
import { warn } from '../../application/console'
import { queryString } from '../../utils/window'
import { AD_TYPES } from '../config/adsConfig'
import BaseClass from '../../application/BaseClass'
import setPageTargeting from '../vms/ads/adTargeting'
import { getIsMobile, getIsDesktop, viewport } from '../../utils/ua'
import { ads } from '../../application/pageDataObject'
import { isCountryCodeUnitedStates } from '../initializers/ads/initAmazonHeaderBidder'
import { getLastKnownCountryCode } from '../../providers/geolocationProvider'

const { adsDebugRefresh } = queryString

let canSetPageTargeting = true
let dfpEventCallbacksRegistered = false

function isPassbackAdType(adType) {
  return adType === AD_TYPES.PASSBACK
}

function getDimensionsObj({ dimensions }) {
  if (getIsDesktop()) {
    return dimensions.desktop
  }
  if (getIsMobile()) {
    return dimensions.mobile
  }
  const { isPortrait } = viewport
  const tabletDimensions = dimensions[isPortrait ? 'portraitTablet' : 'landscapeTablet']
  return tabletDimensions || dimensions.mobile
}

function getDimensions(adData) {
  return getDimensionsObj(adData).map(dimensionsObj => dimensionsObj.valueOf())
}

let isGtagInitialized = false

function repeat(count, string = '0') {
  let padString = ''

  for (let i = 0; i < count; i += 1) {
    padString += string
  }

  return padString
}

function padLeft(string, padding) {
  return repeat(Math.max(0, padding)) + string
}

function getCookie(name) {
  var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
  if (match) return match[2]
  return null
}
function getGoogleConsent() {
  let googleConsent = getCookie('googlepersonalization')
  if (null !== googleConsent && 'undefined' !== typeof googleConsent) {
    let bstr = atob(googleConsent)
    let inputbits = ''

    for (let i = 0; i < bstr.length; i += 1) {
      const bitString = bstr.charCodeAt(i).toString(2)
      inputbits += padLeft(bitString, 8 - bitString.length)
    }

    // convert to bit string 0s and 1s

    return inputbits[72] === '1' ? 0 : 1
  }
  return 0
}

export default class GoogleTagService extends BaseClass {
  constructor(options = {}) {
    super(options)
    let { googletag: gTag } = window
    this.merge({
      gTag: (gTag = gTag || {}),
    })
    gTag.renderedAdsEvents = gTag.renderedAdsEvents || {}
    gTag.cmd = gTag.cmd || []
  }

  globalInitGtag(callback) {
    if (!isGtagInitialized) {
      const { googletag: gTag } = window
      if (gTag) {
        gTag.cmd.push(function initializePubads() {
          if (!isGtagInitialized) {
            isGtagInitialized = true
            gTag.pubads().setRequestNonPersonalizedAds(getGoogleConsent())
            const { pubads } = gTag
            pubads().enableAsyncRendering()
            pubads().collapseEmptyDivs(true)
            if (isCountryCodeUnitedStates(getLastKnownCountryCode())) {
              pubads().disableInitialLoad()
            }
            gTag.enableServices()
          }
          callback()
        })
      }
    }
  }

  get slots() {
    return this.gTag.slots || []
  }

  get renderedAdsEvents() {
    return this.gTag.renderedAdsEvents
  }

  get adUnitPath() {
    const { hierarchyLevels } = ads
    hierarchyLevels.l4 = this.adFormat
    return hierarchyLevels.toString()
  }

  defineSlot(context) {
    const { gTag, adConf, adType, adFormat } = context
    const { slotDivId } = adConf
    const isOutOfPage = adConf.isOutOfPage || false
    let { adUnitPath } = adConf
    adUnitPath = adUnitPath || context.adUnitPath
    const slots = (gTag.slots = gTag.slots || {})
    let slot
    const isPassbackSlot = isPassbackAdType(adType)
    let adDimensions = null
    if (isOutOfPage) {
    } else if (isPassbackSlot) {
      adDimensions = getDimensions(adConf)
      slot = gTag.pubads().definePassback(adUnitPath, adDimensions)
    } else {
      adDimensions = getDimensions(adConf)
      slot = gTag.defineSlot(adUnitPath, adDimensions, slotDivId)
    }
    if (!slot) {
      warn('GoogleTagService.defineSlot: googletag.defineSlot() returned null', {
        adUnitPath,
        adDimensions,
        adConf,
        context,
        isPassbackSlot,
      })
      return
    }
    slots[adFormat] = slot
    if (!isPassbackSlot) {
      slot.addService(gTag.pubads())
    }
  }

  setTargeting(targeting = {}) {
    if (canSetPageTargeting) {
      setPageTargeting(this.gTag, targeting)
      canSetPageTargeting = false
    }
    return this.gTag
  }

  resetPageTargetingLimit() {
    canSetPageTargeting = true
    return this
  }

  refreshSlot(slot) {
    adsDebugRefresh && this.info('Refreshing slot', slot)
    this.setTargeting({ isRefresh: true }).pubads().refresh([slot])
    return this
  }

  initSlot(adConf, adType, adFormat = 'DefaultFormat') {
    this.merge({
      adFormat,
      adConf,
      adType,
    })
      .gTag.cmd // The way DFP invokes the function prohibits the use of
      // proper 'this', so the argument is a workaround
      .push(() => this.defineSlot(this))
    return this
  }

  registerDfpEventCallbacks(onSlotRenderEnded, onSlotOnload) {
    if (!dfpEventCallbacksRegistered) {
      this.gTag.pubads().addEventListener('slotRenderEnded', onSlotRenderEnded)
      this.gTag.pubads().addEventListener('slotOnload', onSlotOnload)
      dfpEventCallbacksRegistered = true
    }
  }

  initTargeting(adData, adFormat, onSlotRenderEnded, onSlotOnload, adType) {
    const { gTag } = this
    gTag.cmd.push(() => {
      const currentTag = gTag.slots[adFormat]
      const isPassbackSlot = isPassbackAdType(adType)
      if (!currentTag) {
        warn(`GoogleTagService.initTargeting: no '${adFormat}' in googletag.slots!`, {
          adData,
          adFormat,
          adType,
          isPassbackSlot,
        })
        return
      }
      if (!isPassbackSlot) {
        currentTag.clearTargeting()
        //slot level targeting
        currentTag.setTargeting('format', adFormat)
      }
      this.registerDfpEventCallbacks(onSlotRenderEnded, onSlotOnload)
      if (!isPassbackSlot) {
        gTag.display(adData.slotDivId)
        setTimeout(() => {
          requestingAds(adFormat)
        }, 0)
      }
    })
    return this
  }
}



// WEBPACK FOOTER //
// ./provolution/app/views/provolution/jsCore/organizedByType/services/GoogleTagService.js